import type { SelectOptionItem } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { groupMessages, type DropdownGroup, type GroupingLabels } from './message-grouping';
import type { PickerMessage } from './message-picker-data';

/**
 * Client-side "virtual" pagination for the message picker: the workspace's
 * full segmentable list is already in memory, so search and load-more slice
 * and group it locally in 50-entry pages instead of re-querying.
 */
export const MESSAGES_PER_PAGE = 50;

export type MessageOption = SelectOptionItem;
export type MessageOptionGroup = {
  /** The source group's identity (parent id / section type), for merging paged results. */
  id: string;
  label: string;
  icon?: IconName;
  options: (MessageOption | MessageOptionGroup)[];
};
export type MessageSelectOption = MessageOption | MessageOptionGroup;

function stringIncludes(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

function transformOptions(items: (PickerMessage | DropdownGroup)[]): MessageSelectOption[] {
  return items.map((item) => {
    if ('isGroup' in item) {
      return {
        id: item.groupId,
        label: item.groupName,
        icon: item.icon,
        options: transformOptions(item.options),
      };
    }
    return {
      value: item.output_id,
      label: item.subject,
      searchLabel: item.searchLabel,
    };
  });
}

export function virtualQuery(
  orderedMessages: PickerMessage[],
  searchText: string,
  page: number,
  labels: GroupingLabels,
): MessageSelectOption[] {
  const start = MESSAGES_PER_PAGE * (page - 1);
  return transformOptions(
    groupMessages(
      orderedMessages
        .filter((message) => stringIncludes(message.searchLabel ?? '', searchText))
        .slice(start, start + MESSAGES_PER_PAGE),
      labels,
    ),
  );
}

/**
 * Which page of the filtered list holds `outputId`, or 1 when the list does
 * not hold it. Opening the picker on a saved selection starts at that page, so
 * the entry renders at its real position in the grouped list instead of being
 * appended past the end.
 */
export function pageContainingMessage(orderedMessages: PickerMessage[], searchText: string, outputId: string): number {
  const index = orderedMessages
    .filter((message) => stringIncludes(message.searchLabel ?? '', searchText))
    .findIndex((message) => message.output_id === outputId);
  return index < 0 ? 1 : Math.floor(index / MESSAGES_PER_PAGE) + 1;
}

/** The filtered list's pages 1..`throughPage`, merged into one grouped list. */
export function queryThroughPage(
  orderedMessages: PickerMessage[],
  searchText: string,
  throughPage: number,
  labels: GroupingLabels,
): MessageSelectOption[] {
  let merged = virtualQuery(orderedMessages, searchText, 1, labels);
  for (let page = 2; page <= throughPage; page += 1) {
    merged = mergeGroupedOptions(merged, virtualQuery(orderedMessages, searchText, page, labels));
  }
  return merged;
}

export function findOptionInList(options: MessageSelectOption[], value: string): boolean {
  for (const option of options) {
    if ('options' in option) {
      if (findOptionInList(option.options, value)) {
        return true;
      }
    } else if (option.value === value) {
      return true;
    }
  }
  return false;
}

/**
 * Merge a further page into the already-displayed grouped options: groups
 * with the same id combine (recursively) — labels can collide across
 * same-named campaigns — and plain options dedupe by value.
 */
export function mergeGroupedOptions(
  existing: MessageSelectOption[],
  incoming: MessageSelectOption[],
): MessageSelectOption[] {
  const result = [...existing];

  for (const item of incoming) {
    if ('options' in item) {
      const existingIndex = result.findIndex((candidate) => 'options' in candidate && candidate.id === item.id);
      const existingGroup = result[existingIndex];
      if (existingGroup != null && 'options' in existingGroup) {
        result[existingIndex] = { ...existingGroup, options: mergeGroupedOptions(existingGroup.options, item.options) };
      } else {
        result.push(item);
      }
    } else {
      const isDuplicate = result.some((candidate) => 'value' in candidate && candidate.value === item.value);
      if (!isDuplicate) {
        result.push(item);
      }
    }
  }

  return result;
}

export function buildInitialSelectOptions(
  orderedMessages: PickerMessage[],
  labels: GroupingLabels,
): MessageSelectOption[] {
  return transformOptions(groupMessages(orderedMessages.slice(0, MESSAGES_PER_PAGE), labels));
}

export function computeHasMoreResults(
  orderedMessages: PickerMessage[],
  searchInputValue: string,
  virtualSelectPage: number,
): boolean {
  const filteredCount =
    searchInputValue === ''
      ? orderedMessages.length
      : orderedMessages.filter((message) => stringIncludes(message.searchLabel ?? '', searchInputValue)).length;
  return virtualSelectPage * MESSAGES_PER_PAGE < filteredCount;
}

/**
 * A saved selection outside the current page still needs an option, or the
 * select renders the value as blank.
 */
export function ensureSelectedOptionPresent(
  options: MessageSelectOption[],
  selectedMessage: PickerMessage | null | undefined,
): MessageSelectOption[] {
  if (selectedMessage == null || findOptionInList(options, selectedMessage.output_id)) {
    return options;
  }
  const [selectedAsOption] = transformOptions([selectedMessage]);
  if (selectedAsOption == null) {
    return options;
  }
  return [...options, selectedAsOption];
}
